import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { StoreSummary } from './AuthContext';
import axios from '../lib/axios';

interface MultiStoreContextType {
  // State
  activeStore: StoreSummary | null;
  userStores: StoreSummary[];
  activeStores: StoreSummary[];
  lastUsedStore: string | null;
  isLoading: boolean;
  
  // Actions
  setActiveStore: (store: StoreSummary) => void;
  setUserStores: (stores: StoreSummary[]) => void;
  switchToStore: (shop: string) => Promise<void>;
  refreshStores: () => Promise<void>;
  clearActiveStore: () => void;
  
  // Helpers
  hasMultipleStores: boolean;
  hasMultipleActiveStores: boolean;
  getStoreByShop: (shop: string) => StoreSummary | undefined;
  getUserRole: () => string;
  isOwner: boolean;
  isMember: boolean;
}

const MultiStoreContext = createContext<MultiStoreContextType | undefined>(undefined);

interface MultiStoreProviderProps {
  children: ReactNode;
}

export function MultiStoreProvider({ children }: MultiStoreProviderProps) {
  const [activeStore, setActiveStoreState] = useState<StoreSummary | null>(null);
  const [userStores, setUserStoresState] = useState<StoreSummary[]>([]);
  const [lastUsedStore, setLastUsedStore] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Filter to get only active/connected stores
  const activeStores = userStores.filter(store => store.connected);

  // Load last used store from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('last_used_store');
    if (stored) {
      setLastUsedStore(stored);
    }
  }, []);

  // Auto-select store when stores are loaded
  useEffect(() => {
    if (userStores.length > 0 && !activeStore) {
      // Try to select last used store first (from all user stores)
      if (lastUsedStore) {
        const lastStore = userStores.find(store => store.shop === lastUsedStore);
        if (lastStore) {
          setActiveStoreState(lastStore);
          return;
        }
      }
      
      // Otherwise, select the first available store (connected or not)
      setActiveStoreState(userStores[0]);
    }
  }, [userStores, activeStore, lastUsedStore]);

  const setActiveStore = (store: StoreSummary) => {
    setActiveStoreState(store);
    setLastUsedStore(store.shop);
    localStorage.setItem('last_used_store', store.shop);
    localStorage.setItem('shop', store.shop);
  };

  // Authoritative store list source. AuthContext owns the real /me payload
  // (which includes user_role + permissions); MultiStoreContext mirrors it so
  // role/permission consumers (useStorePermissions, useEnhancedPermissions,
  // DynamicSidebar, ...) never read an empty store list.
  const setUserStores = (stores: StoreSummary[]) => {
    setUserStoresState(stores);
  };

  const switchToStore = async (shop: string): Promise<void> => {
    const store = userStores.find(s => s.shop === shop);
    if (!store) {
      throw new Error(`Store ${shop} not found in user's stores`);
    }

    setIsLoading(true);
    try {
      // Call API to switch store context
      await axios.post(`/stores/${store.id}/switch`);

      // Update local state
      setActiveStore(store);
      
      // Dispatch event for other components to listen to
      window.dispatchEvent(new CustomEvent('store_switched', {
        detail: { store: store.shop, role: store.user_role }
      }));
      
    } catch (error) {
      console.error('Error switching store:', error);
      const errorMessage = axios.isAxiosError(error) 
        ? error.response?.data?.message || error.message || 'Failed to switch store'
        : error instanceof Error 
        ? error.message 
        : 'Failed to switch store';
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshStores = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const response = await axios.get('/user/stores');
      const stores = response.data;
      setUserStoresState(stores);
    } catch (error) {
      console.error('Error refreshing stores:', error);
      const errorMessage = axios.isAxiosError(error) 
        ? error.response?.data?.message || error.message || 'Failed to fetch stores'
        : error instanceof Error 
        ? error.message 
        : 'Failed to fetch stores';
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const clearActiveStore = () => {
    setActiveStoreState(null);
    localStorage.removeItem('last_used_store');
    localStorage.removeItem('shop');
  };

  const getStoreByShop = (shop: string): StoreSummary | undefined => {
    return userStores.find(store => store.shop === shop);
  };

  const getUserRole = (): string => {
    if (!activeStore) return 'unknown';
    return activeStore.user_role || 'member';
  };

  const hasMultipleStores = userStores.length > 1;
  const hasMultipleActiveStores = activeStores.length > 1;
  const isOwner = getUserRole() === 'owner';
  const isMember = getUserRole() === 'member';

  const value: MultiStoreContextType = {
    // State
    activeStore,
    userStores,
    activeStores,
    lastUsedStore,
    isLoading,
    
    // Actions
    setActiveStore,
    setUserStores,
    switchToStore,
    refreshStores,
    clearActiveStore,
    
    // Helpers
    hasMultipleStores,
    hasMultipleActiveStores,
    getStoreByShop,
    getUserRole,
    isOwner,
    isMember,
  };

  return (
    <MultiStoreContext.Provider value={value}>
      {children}
    </MultiStoreContext.Provider>
  );
}

export function useMultiStore(): MultiStoreContextType {
  const context = useContext(MultiStoreContext);
  if (context === undefined) {
    throw new Error('useMultiStore must be used within a MultiStoreProvider');
  }
  return context;
}

export default MultiStoreContext;
